<template>
	<v-heading>{{ t('sidebar.additionalInfo') }}</v-heading>
	<v-sub-heading>{{ t('sidebar.cutoffDateMessageStart') }} {{ jobStore.job.numDaysUpdateSong }} {{ t('sidebar.cutoffDateMessageEnd') }}</v-sub-heading>
	<v-sub-heading>{{ t('sidebar.emailMessage') }}</v-sub-heading>

	<tw-input
		@input="() => emailError = ''"
		v-model="groomEmail"
		:is-valid="!emailError?.length"
		:label="t('sidebar.groomEmail')"
		name="groomEmail"
		:validation="emailError" />

	<tw-input
		@input="() => emailError = ''"
		v-model="brideEmail"
		:is-valid="!emailError?.length"
		:label="t('sidebar.brideEmail')"
		name="brideEmail"
		:validation="emailError" />

	<v-sub-heading class="-mb-4">
		{{ t('confirmationChecklist.title') }}
	</v-sub-heading>
	<div class="px-4">
		<ul class="list-disc marker:text-zinc-500">
			<li><v-text>{{ t('confirmationChecklist.completedSelection') }}</v-text></li>
			<li><v-text>{{ t('confirmationChecklist.deadline') }}</v-text></li>
			<li><v-text>{{ t('confirmationChecklist.customSong') }}</v-text></li>
			<li><v-text>{{ t('confirmationChecklist.blankFields') }}</v-text></li>
			<li><v-text>{{ t('confirmationChecklist.bgm') }}</v-text></li>
		</ul>
		<v-checkbox-field>
			<v-checkbox
				v-model="submissionConfirmation" 
				class="my-6"
				color="teal"
				id="submission-confirmation"
				name="submission-confirmation" />
			<v-label>{{ t('confirmationChecklist.confirmation') }}</v-label>
		</v-checkbox-field>
	</div>

	<v-error-message v-if="emailError?.length">
		{{ emailError }}
	</v-error-message>

	<v-error-message v-if="scriptError?.length">
		{{ scriptError }}
	</v-error-message>

	<div class="block md:hidden pb-8">
		<tw-button 
			@click="navigateNext"
			class="btn-brand font-medium w-full mb-6"
			:disabled="!submissionConfirmation"
			:loading="loading">
			{{ t('sidebar.submit') }}
		</tw-button>
		<tw-button
			@click="() => emit('navigateBack')"  
			class="font-medium w-full"
			class-name="text-blue-500"
			:loading="loading">
			{{ t('sidebar.back') }}
		</tw-button>
	</div>
</template>

<script lang="ts">
	export default { name: 'AdditionalInfo' } // name the component
</script>

<script setup lang="ts">

	// Auth
	import { 
		Auth
	} from '@/api/Auth'

	// Language
	import {
		useI18n
	} from 'vue-i18n'

	// Router
	import {
		useRouter
	} from 'vue-router'

	// Store
	import { 
		store as _jobStore
	} from '@/store/job'
	import {
		localesStore as _localesStore
	} from '@/store/locales'

	// Vue
	import {
		computed,
		ref
	} from 'vue'

	const {t} = useI18n()
	const jobStore = _jobStore()
	const localesStore = _localesStore()
	const router = useRouter()

	const notes = computed(() => {
		if(localesStore.locales.selected === 'ja') return jobStore.job.jobDetails.notesKanji
		else if(localesStore.locales.selected === 'kana') return jobStore.job.jobDetails.notesKana
		return jobStore.job.jobDetails.notesEnglish
	})

	const groomEmail = ref<string | null>(null)
	const brideEmail = ref<string | null>(null)
	const emailError = ref<string>('')
	const scriptError = ref<string>('')
	const loading = ref<boolean>(false)
	const submissionConfirmation = ref(false)

	async function navigateNext() {
		emailError.value = ''
		scriptError.value = ''

		// Verify either the bride/groom email has been entered
		if((!groomEmail.value || !groomEmail.value?.length) && (!brideEmail.value || !brideEmail.value?.length)) {
			emailError.value = t('sidebar.emailError')
			return
		}

		loading.value = true

		try {
			const jobId = jobStore.job?.jobID
			const scenesArray = jobStore.job?.jobDetails?.scenes
			const scenes = scenesArray?.map(scene => { 
				return { 
					sceneId: scene['job__JOBS_SCENES__jobID::_kp_jobs_scenes_id'], 
					songId: scene['job__JOBS_SCENES__jobID::zctSongID'],
					customSongName: scene.customSong?.songName ?? '',
					customSongArtist: scene.customSong?.mainArtist ?? '',
					customSongAlbum: scene.customSong?.albumName ?? '',
					customSongTrack: scene.customSong?.track ?? '',
				}
			})
			const res = await Auth.submitSongs(jobId, scenes, brideEmail.value, groomEmail.value, notes.value)
			const scriptResult = JSON.parse(res?.response?.scriptResult ?? '{}')

			if(scriptResult.errorCode != 0 && !res.messages[0].code.includes('Operation timed out')) {
				const error = scriptResult.errorMessage
				throw error
			}

			router.push({name: "SongSubmit"})
		}
		catch(e) {
			console.log(e)
			scriptError.value = e
		}
		finally {
			loading.value = false
		}
		
	}

	const emit = defineEmits([
		'navigateBack'
	])

	defineExpose({
		loading,
		navigateNext,
		submissionConfirmation
	})

  </script>